import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const supports = [
  {
    icon: 'ri-file-paper-2-line',
    titleKey: 'support.doc.title',
    descKey: 'support.doc.desc',
  },
  {
    icon: 'ri-translate-2',
    titleKey: 'support.lang.title',
    descKey: 'support.lang.desc',
  },
  {
    icon: 'ri-store-3-line',
    titleKey: 'support.space.title',
    descKey: 'support.space.desc',
  },
  {
    icon: 'ri-group-2-line',
    titleKey: 'support.community.title',
    descKey: 'support.community.desc',
  },
  {
    icon: 'ri-megaphone-line',
    titleKey: 'support.marketing.title',
    descKey: 'support.marketing.desc',
  },
  {
    icon: 'ri-shield-check-line',
    titleKey: 'support.security.title',
    descKey: 'support.security.desc',
  },
];

const SupportSection = () => {
  const { t } = useTranslation('choHem');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section className="py-14 md:py-24 bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-16">
          <span className="inline-block px-3 py-1 bg-amber-500/15 border border-amber-400/30 rounded-full text-xs text-amber-300 font-semibold uppercase tracking-wider mb-3">
            {t('support.badge')}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white font-serif mb-3">
            {t('support.title')} <span className="text-amber-400">{t('support.titleHighlight')}</span>
          </h2>
          <p className="text-gray-400 text-sm md:text-base max-w-2xl mx-auto">
            {t('support.subtitle')}
          </p>
        </div>

        {/* Desktop grid */}
        <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {supports.map((s, i) => (
            <div
              key={i}
              className="p-5 md:p-6 bg-white/5 border border-white/10 rounded-2xl hover:border-amber-400/30 hover:bg-amber-500/5 transition-all duration-300 group"
            >
              <div className="w-11 h-11 flex items-center justify-center bg-amber-500/15 rounded-xl mb-4 group-hover:bg-amber-500/25 transition-colors">
                <i className={`${s.icon} text-amber-400 text-xl`}></i>
              </div>
              <h3 className="text-white font-bold text-sm md:text-base mb-2">{t(s.titleKey)}</h3>
              <p className="text-gray-400 text-xs md:text-sm leading-relaxed">{t(s.descKey)}</p>
            </div>
          ))}
        </div>

        {/* Mobile accordion */}
        <div className="flex flex-col gap-2 sm:hidden">
          {supports.map((s, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                className={`border rounded-xl overflow-hidden transition-all duration-300 ${isOpen ? 'border-amber-400/40 bg-amber-500/5' : 'border-white/10 bg-white/5'}`}
              >
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-center justify-between px-4 py-3.5 cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${isOpen ? 'bg-amber-500/25' : 'bg-amber-500/15'}`}>
                      <i className={`${s.icon} text-amber-400 text-base`}></i>
                    </div>
                    <span className="text-white font-semibold text-sm">{t(s.titleKey)}</span>
                  </div>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`ri-arrow-down-s-line text-amber-400 text-lg transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}></i>
                  </div>
                </button>
                <div
                  className={`transition-all duration-300 overflow-hidden ${isOpen ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <p className="text-gray-400 text-xs leading-relaxed px-4 pb-4">
                    {t(s.descKey)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SupportSection;